import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { Fields } from '../components'

const validationSchema = yup
  .object({
    postalAddressOne: yup.string().required('Please enter your street address'),
    postalAddressTwo: yup.string().notRequired(),
    postalAddressCity: yup.string().required('Please enter your city/suburb'),
    postalAddressPostCode: yup
      .string()
      .required('Please enter your postcode')
      .matches(/^\d*$/, 'Please enter a valid postcode')
      .min(4, 'Please enter a valid postcode')
      .max(6, 'Please enter a valid postcode'),
    postalAddressState: yup.object().required('Please enter your state'),
    postalAddressCountry: yup.object().required('Please enter your country'),
    identicalBillingAddress: yup.boolean(),
    billingAddressOne: yup.string().when('identicalBillingAddress', {
      is: false,
      then: yup.string().required('Please enter your billing address'),
      otherwise: yup.string().notRequired(),
    }),
    billingAddressTwo: yup.string().notRequired(),
    billingAddressCity: yup.string().when('identicalBillingAddress', {
      is: false,
      then: yup.string().required('Please enter your city'),
      otherwise: yup.string().notRequired(),
    }),
    billingAddressPostCode: yup.string().when('identicalBillingAddress', {
      is: false,
      then: yup
        .string()
        .required('Please enter your postcode')
        .matches(/^\d*$/, 'Please enter a valid postcode')
        .min(4, 'Please enter a valid postcode')
        .max(6, 'Please enter a valid postcode'),
      otherwise: yup.string().notRequired(),
    }),
    billingAddressState: yup.object().when('identicalBillingAddress', {
      is: false,
      then: yup.object().required('Please enter your state'),
      otherwise: yup.object().notRequired(),
    }),
    billingAddressCountry: yup.object().when('identicalBillingAddress', {
      is: false,
      then: yup.object().required('Please enter your country'),
      otherwise: yup.object().notRequired(),
    }),
  })
  .required()

const formSchema = {
  sections: [
    {
      id: 'address',
      title: 'Your address',
      fieldKeys: [
        'postalAddressOne',
        'postalAddressTwo',
        'postalAddressCity',
        'postalAddressPostCode',
        'postalAddressState',
        'postalAddressCountry',
      ],
      conditional: false,
    },
    {
      id: 'billing',
      title: 'Billing address',
      fieldKeys: [
        'identicalBillingAddress',
        'billingAddressOne',
        'billingAddressTwo',
        'billingAddressCity',
        'billingAddressPostCode',
        'billingAddressState',
        'billingAddressCountry',
      ],
      conditional: true,
    },
  ],
  fields: [
    {
      id: 'postalAddressOne',
      label: 'Address line 1 *',
      autocomplete: 'given-name',
      type: 'text',
      cssClass: 'fullWidth',
      conditional: false,
      isConditionalFlag: false,
    },
    {
      id: 'postalAddressTwo',
      label: 'Address line 2',
      autocomplete: 'additional-name',
      type: 'text',
      cssClass: 'fullWidth',
      conditional: false,
      isConditionalFlag: false,
    },
    {
      id: 'postalAddressCity',
      label: 'City *',
      autocomplete: 'family-name',
      type: 'text',
      conditional: false,
      isConditionalFlag: false,
    },
    {
      id: 'postalAddressPostCode',
      label: 'Postcode *',
      autocomplete: 'bday',
      type: 'text',
      conditional: false,
      isConditionalFlag: false,
    },
    {
      id: 'postalAddressState',
      label: 'State *',
      autocomplete: 'bday',
      type: 'select',
      selectOptions: 'stateList',
      conditional: false,
      isConditionalFlag: false,
    },
    {
      id: 'postalAddressCountry',
      label: 'Country *',
      autocomplete: 'bday',
      type: 'select',
      selectOptions: 'countryList',
      conditional: false,
      isConditionalFlag: false,
    },
    {
      id: 'identicalBillingAddress',
      label: 'Billing address same as above',
      autocomplete: '',
      type: 'checkbox',
      cssClass: 'fullWidth',
      defaultChecked: true,
      conditional: false,
      isConditionalFlag: true,
    },
    {
      id: 'billingAddressOne',
      label: 'Address line 1 *',
      autocomplete: 'given-name',
      type: 'text',
      cssClass: 'fullWidth',
      conditional: true,
      isConditionalFlag: false,
    },
    {
      id: 'billingAddressTwo',
      label: 'Address line 2',
      autocomplete: 'additional-name',
      type: 'text',
      cssClass: 'fullWidth',
      conditional: true,
      isConditionalFlag: false,
    },
    {
      id: 'billingAddressCity',
      label: 'City *',
      autocomplete: 'family-name',
      type: 'text',
      conditional: true,
      isConditionalFlag: false,
    },
    {
      id: 'billingAddressPostCode',
      label: 'Postcode *',
      autocomplete: 'bday',
      type: 'text',
      conditional: true,
      isConditionalFlag: false,
    },
    {
      id: 'billingAddressState',
      label: 'State *',
      autocomplete: 'bday',
      type: 'select',
      selectOptions: 'stateList',
      conditional: true,
      isConditionalFlag: false,
    },
    {
      id: 'billingAddressCountry',
      label: 'Country *',
      autocomplete: 'bday',
      type: 'select',
      selectOptions: 'countryList',
      conditional: true,
      isConditionalFlag: false,
    },
  ],
}

export default props => {
  const { formIndex, storeFormData, changeTabs, formData } = props

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...formData,
    },
    resolver: yupResolver(validationSchema),
  })
  const onSubmit = data => {
    storeFormData(formIndex, data)
  }

  const [showFields, setShowFields] = useState({})
  const handleShowFields = (id, checked) => {
    setShowFields({
      ...showFields,
      [id]: checked,
    })
  }

  useEffect(() => {
    let showFieldSections = {}

    formSchema.sections.forEach(section => {
      showFieldSections[section.id] = !section.conditional
    })

    setShowFields(showFieldSections)
  }, [])

  const goToPrevious = () => {
    changeTabs('prev', formIndex)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {formSchema.sections.map(section => (
        <div className="formSection" key={`section-${section.id}`}>
          <h3>{section.title}</h3>

          {section.fieldKeys.map(fieldKey => {
            const props = formSchema.fields.filter(f => {
              return f.id === fieldKey
            })[0]
            const Component = Fields[props.type] ?? 'unknown'

            if (!props.isConditionalFlag && !showFields[section.id]) return

            return (
              <Component
                key={fieldKey}
                sectionId={section.id}
                control={control}
                register={register}
                field={props}
                errors={errors}
                handleShowFields={handleShowFields}
              />
            )
          })}
        </div>
      ))}

      <div className="formControls">
        <div className="button buttonPrevious" onClick={goToPrevious}>
          Back
        </div>
        <button className="button buttonNext" type="submit">
          Next
        </button>
      </div>
    </form>
  )
}
